import { useCallback, useEffect, useMemo, useState } from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { DateTime } from "luxon"
import { ArrowUpRight, ArrowDownRight, Equal } from "lucide-react"
import { BillingTrendBucket } from "../../types"
import { numberToUSD } from "../../common/utils"
import bbApi from "../../api"
import { ListSkeleton } from "../../common/ui"

interface Props {
  status?: string
  orgName?: string
  excludeZero?: boolean
  onMonthClick?: (month: string) => void
}

const BillingTrendChart = ({
  status,
  orgName,
  excludeZero,
  onMonthClick
}: Props) => {
  const [buckets, setBuckets] = useState<BillingTrendBucket[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const fetchTrend = useCallback(async () => {
    setIsLoading(true)
    try {
      const now = DateTime.now()
      const from = now.minus({ months: 24 }).startOf("month")
      const to = now.minus({ months: 1 }).endOf("month")

      const data = await bbApi.billing.fetchBillingTrend({
        interval: "month",
        from: Math.floor(from.toSeconds()),
        to: Math.floor(to.toSeconds()),
        status: status || undefined,
        org_name: orgName?.trim() || undefined,
        exclude_zero: excludeZero || undefined
      })
      setBuckets(data.buckets ?? [])
    } catch {
      setBuckets([])
    } finally {
      setIsLoading(false)
    }
  }, [status, orgName, excludeZero])

  useEffect(() => {
    fetchTrend()
  }, [fetchTrend])

  const { currentMonths, previousMonths, monthLabels } = useMemo(() => {
    const now = DateTime.now()
    const currentData: BillingTrendBucket[] = []
    const previousData: BillingTrendBucket[] = []
    const labels: string[] = []

    for (let i = 12; i >= 1; i--) {
      const currentDt = now.minus({ months: i }).startOf("month")
      const previousDt = currentDt.minus({ months: 12 })
      const currentLabel = currentDt.toFormat("yyyy-MM")
      const previousLabel = previousDt.toFormat("yyyy-MM")

      labels.push(currentDt.toFormat("LLL"))

      const currentBucket = buckets.find(b => b.label === currentLabel)
      currentData.push(
        currentBucket ?? {
          timestamp: Math.floor(currentDt.toSeconds()),
          label: currentLabel,
          total_value: 0,
          invoice_count: 0
        }
      )

      const previousBucket = buckets.find(b => b.label === previousLabel)
      previousData.push(
        previousBucket ?? {
          timestamp: Math.floor(previousDt.toSeconds()),
          label: previousLabel,
          total_value: 0,
          invoice_count: 0
        }
      )
    }

    return {
      currentMonths: currentData,
      previousMonths: previousData,
      monthLabels: labels
    }
  }, [buckets])

  const { yoyChange, currentTotal, previousTotal } = useMemo(() => {
    const curr = currentMonths.reduce((sum, b) => sum + b.total_value, 0)
    const prev = previousMonths.reduce((sum, b) => sum + b.total_value, 0)
    const change =
      prev === 0 ? (curr > 0 ? 100 : 0) : ((curr - prev) / prev) * 100
    return { yoyChange: change, currentTotal: curr, previousTotal: prev }
  }, [currentMonths, previousMonths])

  const currentRangeLabel = "Current 12 mo"
  const previousRangeLabel = "Prior 12 mo"

  const chartOptions = useMemo((): Highcharts.Options => {
    return {
      chart: {
        type: "areaspline",
        height: 280,
        spacing: [10, 0, 10, 0],
        style: { fontFamily: "inherit" },
        reflow: true
      },
      title: { text: "" },
      credits: { enabled: false },
      xAxis: {
        categories: monthLabels,
        labels: {
          style: { color: "#9CA3AF", fontSize: "11px" }
        },
        lineColor: "#E5E7EB",
        tickLength: 0,
        crosshair: {
          color: "rgba(40, 29, 87, 0.08)",
          width: 32
        }
      },
      yAxis: {
        title: { text: "" },
        labels: {
          style: { color: "#9CA3AF", fontSize: "11px" },
          x: -8,
          formatter: function () {
            const val = this.value as number
            if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`
            if (val >= 1000) return `$${(val / 1000).toFixed(0)}k`
            return `$${val}`
          }
        },
        gridLineColor: "#F3F4F6",
        gridLineDashStyle: "Dot"
      },
      tooltip: {
        backgroundColor: "#281D57",
        borderColor: "#281D57",
        borderRadius: 8,
        shadow: false,
        shared: true,
        useHTML: true,
        style: { color: "#ffffff", fontSize: "12px" },
        formatter: function () {
          const idx = this.points?.[0]?.point?.index ?? 0
          const curr = currentMonths[idx]?.total_value ?? 0
          const prev = previousMonths[idx]?.total_value ?? 0
          const currLabel = DateTime.fromFormat(
            currentMonths[idx]?.label ?? "",
            "yyyy-MM"
          ).toFormat("LLL yyyy")
          const prevLabel = DateTime.fromFormat(
            previousMonths[idx]?.label ?? "",
            "yyyy-MM"
          ).toFormat("LLL yyyy")

          let pctHtml = ""
          if (prev > 0) {
            const pct = ((curr - prev) / prev) * 100
            const color = pct >= 0 ? "#63C7C4" : "#F87171"
            const sign = pct >= 0 ? "+" : ""
            pctHtml = `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.15);color:${color};font-weight:600">${sign}${pct.toFixed(
              1
            )}% vs prior year</div>`
          }

          return `
            <div style="padding:4px 2px">
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
                <span style="width:8px;height:3px;background:#281D57;border-radius:2px;display:inline-block"></span>
                <span>${currLabel}:</span>
                <span style="font-weight:600;margin-left:auto">${numberToUSD(
                  curr
                )}</span>
              </div>
              <div style="display:flex;align-items:center;gap:6px;opacity:0.7">
                <span style="width:8px;height:3px;background:#9CA3AF;border-radius:2px;display:inline-block;border:1px dashed #9CA3AF"></span>
                <span>${prevLabel}:</span>
                <span style="font-weight:600;margin-left:auto">${numberToUSD(
                  prev
                )}</span>
              </div>
              ${pctHtml}
            </div>
          `
        }
      },
      plotOptions: {
        series: {
          cursor: onMonthClick ? "pointer" : undefined,
          point: {
            events: {
              click: function () {
                if (onMonthClick) {
                  const idx = this.index
                  const label = currentMonths[idx]?.label
                  if (label) onMonthClick(label)
                }
              }
            }
          }
        },
        areaspline: {
          fillOpacity: 0.08,
          lineWidth: 2.5,
          marker: {
            enabled: false,
            symbol: "circle",
            radius: 4,
            lineWidth: 2,
            lineColor: "#ffffff",
            states: {
              hover: { enabled: true, radius: 5 }
            }
          }
        },
        spline: {
          lineWidth: 1.5,
          marker: {
            enabled: false,
            states: {
              hover: { enabled: true, radius: 3 }
            }
          }
        }
      },
      legend: { enabled: false },
      responsive: {
        rules: [
          {
            condition: { maxWidth: 500 },
            chartOptions: {
              chart: { height: 220, spacing: [8, 0, 8, 0] },
              xAxis: {
                labels: { step: 2, style: { fontSize: "10px" } }
              },
              yAxis: {
                labels: { x: -4, style: { fontSize: "10px" } },
                tickAmount: 4
              }
            }
          }
        ]
      },
      series: [
        {
          type: "spline",
          name: previousRangeLabel,
          color: "#D1D5DB",
          dashStyle: "ShortDash",
          data: previousMonths.map(b => b.total_value),
          zIndex: 1
        },
        {
          type: "areaspline",
          name: currentRangeLabel,
          color: "#281D57",
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, "rgba(40, 29, 87, 0.12)"],
              [1, "rgba(40, 29, 87, 0.01)"]
            ]
          } as unknown as string,
          data: currentMonths.map(b => b.total_value),
          zIndex: 2
        }
      ]
    }
  }, [
    monthLabels,
    currentMonths,
    previousMonths,
    currentRangeLabel,
    previousRangeLabel,
    onMonthClick
  ])

  const hasData = useMemo(() => {
    return (
      currentMonths.some(b => b.total_value > 0) ||
      previousMonths.some(b => b.total_value > 0)
    )
  }, [currentMonths, previousMonths])

  if (isLoading) {
    return <ListSkeleton containerClassName="rounded-lg overflow-hidden mb-4" />
  }

  if (!hasData) return null

  const isPositive = yoyChange > 0
  const isNegative = yoyChange < 0

  return (
    <div className="bg-white rounded-lg border border-neutral-200 p-4 sm:p-6 mb-4">
      <div className="mb-4">
        <h3 className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-1">
          Revenue Trend
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-2xl font-semibold text-primary-alt-400 tabular-nums">
            {numberToUSD(currentTotal)}
          </span>
          <div
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold tabular-nums ${
              isPositive
                ? "bg-emerald-50 text-emerald-600"
                : isNegative
                ? "bg-red-50 text-red-600"
                : "bg-neutral-100 text-neutral-500"
            }`}
          >
            {isPositive ? (
              <ArrowUpRight className="w-3.5 h-3.5" />
            ) : isNegative ? (
              <ArrowDownRight className="w-3.5 h-3.5" />
            ) : (
              <Equal className="w-3.5 h-3.5" />
            )}
            {yoyChange > 0 ? "+" : ""}
            {yoyChange.toFixed(1)}%
          </div>
          <span className="text-sm text-neutral-400">
            vs {numberToUSD(previousTotal)} prior year
          </span>
        </div>
      </div>

      <div className="flex items-center gap-5 mb-3">
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <span className="w-5 h-[3px] rounded bg-primary-alt-400 inline-block" />
          {currentRangeLabel}
        </div>
        <div className="flex items-center gap-2 text-xs text-neutral-400">
          <span className="w-5 h-0 border-t-[2px] border-dashed border-neutral-300 inline-block" />
          {previousRangeLabel}
        </div>
      </div>

      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </div>
  )
}

export default BillingTrendChart
